import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Divider,
  InputAdornment,
  Fab,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  SmartToy as AIIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { grokAI, type GrokMessage } from '../services/grokAI';
import { feedbackService } from '../services/feedback';
import { telegram } from '../services/telegram';
import { featureAccess } from '../services/featureAccess';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const AISpeedDial: React.FC = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll to bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);
    setIsStreaming(true);

    // Add empty assistant message that we'll stream into
    const assistantMessage: Message = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      // Build conversation history for context
      const conversationHistory: GrokMessage[] = messages.map(msg => ({
        role: msg.role,
        content: msg.content,
      }));
      conversationHistory.push({ role: 'user', content: userMessage.content });

      // Add system prompt for oil & gas context
      const systemMessage: GrokMessage = {
        role: 'system',
        content: `You are a helpful AI assistant for ZiaWellNav, an oil & gas well navigation app in the Permian Basin of New Mexico and Texas. 
        
You help users with:
- Finding wells, rigs, SWD (saltwater disposal) facilities
- Understanding oil & gas terminology
- Navigation and directions to facilities
- General questions about the oil & gas industry

Be concise, helpful, and knowledgeable about the Permian Basin oil & gas industry.`,
      };

      // Use streaming for better UX
      let fullResponse = '';
      const stream = grokAI.chatStream(
        [systemMessage, ...conversationHistory],
        { model: 'grok-4-fast', temperature: 0.7, maxTokens: 1024 }
      );

      for await (const chunk of stream) {
        fullResponse += chunk;
        // Update the last message with streamed content
        setMessages(prev => {
          const updated = [...prev];
          if (updated.length > 0) {
            updated[updated.length - 1] = {
              ...updated[updated.length - 1],
              content: fullResponse,
            };
          }
          return updated;
        });
      }
    } catch (error) {
      console.error('Grok API error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      
      // Update the assistant message with error
      setMessages(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: `Sorry, I encountered an error: ${errorMessage}. Please check your API key and try again.`,
          };
        }
        return updated;
      });
    } finally {
      setIsLoading(false);
      setIsStreaming(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleOpen = async () => {
    feedbackService.playSound('tap');
    telegram.hapticFeedback('light');
    
    // Check premium feature access for AI Assistant
    const hasAccess = await featureAccess.checkAccessWithPrompt('aiAssistant');
    if (!hasAccess) {
      console.log('🔒 [AI] AI Assistant locked - trial expired');
      return;
    }
    
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      {/* AI FAB Button - Grok Robot Icon */}
      <Tooltip title="Grok AI Assistant" placement="left">
        <Fab
          size="medium"
          onClick={handleOpen}
          sx={{
            width: 56,
            height: 56,
            background: isDarkMode 
              ? 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' 
              : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            border: isDarkMode 
              ? '5px solid #00ffff'
              : '5px solid #8a2be2',
            boxShadow: isDarkMode
              ? '0 4px 20px rgba(0, 255, 255, 0.3)'
              : '0 4px 20px rgba(138, 43, 226, 0.25)',
            '&:hover': {
              background: isDarkMode
                ? 'linear-gradient(135deg, #1f1f3a 0%, #1a2744 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)',
              boxShadow: isDarkMode
                ? '0 6px 25px rgba(0, 255, 255, 0.4)'
                : '0 6px 25px rgba(138, 43, 226, 0.35)',
              transform: 'scale(1.08)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <AIIcon 
            sx={{ 
              fontSize: 28,
              color: isDarkMode ? '#ffffff' : undefined,
              background: isDarkMode
                ? 'none'
                : 'linear-gradient(135deg, #0891b2 0%, #7c3aed 50%, #db2777 100%)',
              WebkitBackgroundClip: isDarkMode ? 'unset' : 'text',
              WebkitTextFillColor: isDarkMode ? '#ffffff' : 'transparent',
              backgroundClip: isDarkMode ? 'unset' : 'text',
              filter: isDarkMode 
                ? 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.5))'
                : 'drop-shadow(0 0 4px rgba(124, 58, 237, 0.4))',
              animation: 'grokPulse 2s ease-in-out infinite',
              '@keyframes grokPulse': {
                '0%, 100%': { 
                  filter: isDarkMode 
                    ? 'drop-shadow(0 0 6px rgba(255, 255, 255, 0.5))'
                    : 'drop-shadow(0 0 4px rgba(124, 58, 237, 0.4))'
                },
                '50%': { 
                  filter: isDarkMode
                    ? 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.7))'
                    : 'drop-shadow(0 0 8px rgba(124, 58, 237, 0.6))'
                }
              }
            }} 
          />
        </Fab>
      </Tooltip>

      {/* Main Chat Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'linear-gradient(135deg, rgba(18, 18, 18, 0.98) 0%, rgba(30, 30, 30, 0.98) 100%)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            borderRadius: 3,
            boxShadow: '0 0 30px rgba(0, 255, 255, 0.2)',
            height: '70vh',
            maxHeight: '600px',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, rgba(0, 255, 255, 0.1) 0%, rgba(138, 43, 226, 0.1) 100%)',
            borderBottom: '1px solid rgba(0, 255, 255, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AIIcon sx={{ color: 'cyan', fontSize: 28 }} />
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
              Grok AI Assistant
            </Typography>
          </Box>
          <Box>
            <IconButton
              onClick={handleClose}
              sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'cyan' } }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            p: 0,
            height: '100%',
          }}
        >
          {/* Messages Area */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'rgba(255, 255, 255, 0.5)',
                  textAlign: 'center',
                  px: 3,
                }}
              >
                <AIIcon sx={{ fontSize: 64, color: 'rgba(0, 255, 255, 0.3)', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Welcome to Grok AI
                </Typography>
                <Typography variant="body2">
                  Ask me anything about wells, facilities, or the oil & gas industry in the Permian Basin!
                </Typography>
              </Box>
            )}

            {messages.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  gap: 1,
                }}
              >
                {message.role === 'assistant' && (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, cyan, #8a2be2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <AIIcon sx={{ fontSize: 18, color: 'white' }} />
                  </Box>
                )}
                <Paper
                  sx={{
                    p: 1.5,
                    maxWidth: '75%',
                    background:
                      message.role === 'user'
                        ? 'linear-gradient(135deg, rgba(0, 255, 255, 0.2), rgba(138, 43, 226, 0.2))'
                        : 'rgba(255, 255, 255, 0.05)',
                    border:
                      message.role === 'user'
                        ? '1px solid rgba(0, 255, 255, 0.3)'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'white',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {message.content}
                    {isStreaming && index === messages.length - 1 && message.role === 'assistant' && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          width: 8,
                          height: 16,
                          bgcolor: 'cyan',
                          ml: 0.5,
                          animation: 'blink 1s infinite',
                          '@keyframes blink': {
                            '0%, 50%': { opacity: 1 },
                            '51%, 100%': { opacity: 0 },
                          },
                        }}
                      />
                    )}
                  </Typography>
                </Paper>
                {message.role === 'user' && (
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #ff6b6b, #ff8e53)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 18, color: 'white' }} />
                  </Box>
                )}
              </Box>
            ))}
            <div ref={messagesEndRef} />
          </Box>

          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Input Area */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask Grok anything..."
              disabled={isLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'rgba(0, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(0, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'cyan',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSendMessage}
                      disabled={!inputValue.trim() || isLoading}
                      sx={{
                        color: inputValue.trim() && !isLoading ? 'cyan' : 'rgba(255, 255, 255, 0.3)',
                        '&:hover': {
                          color: 'cyan',
                          bgcolor: 'rgba(0, 255, 255, 0.1)',
                        },
                      }}
                    >
                      {isLoading ? (
                        <CircularProgress size={24} sx={{ color: 'cyan' }} />
                      ) : (
                        <SendIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Removed API Key Dialog - using server-side authentication */}
    </>
  );
};

export default AISpeedDial;
